import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { ContrastIcon, StarIcon, TextSizeIcon } from '@treams/treams-icons';
import { Card, Highlight, Paragraph, PlainInput, Tags, WorkingArea } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useLocalizedState from 'packages/kakapo-ui-application/hooks/useLocalizedState';

export default function FeedbackQuestionCard(props: {
	item: graphql.FeedbackQuestion;
	new?: boolean;
	query?: string;
	selected?: boolean;
	readOnly?: boolean;
	buttons?: ReactNode;
	onCancel?: () => Promise<void> | void;
	onCreate?: (item: graphql.FeedbackQuestion) => void;
	onUpdate?: (item: graphql.FeedbackQuestion) => void;
	onSelect?: (item: graphql.FeedbackQuestion) => void;
}) {
	const { t } = useTranslation();

	const [editing, setEditing] = useState(props.new ?? false);
	const [autoFocus, setAutoFocus] = useState('text');
	const [text, setText] = useState(props.item.text);
	const [localizedText, setLocalizedText] = useLocalizedState(text, setText);
	const [type, setType] = useState(props.item.type);

	const [create] = graphql.useCreateFeedbackQuestionMutation();
	const [update] = graphql.useUpdateFeedbackQuestionMutation();

	return (
		<WorkingArea
			active={editing}
			onCancel={() => {
				setText(props.item.text);
				setType(props.item.type);
				props.onCancel?.();
				setEditing(false);
			}}
			onSave={async () => {
				if (props.new) {
					const response = await create({
						variables: { input: { categoryID: props.item.category.id, text, type } },
					});

					props.onCreate?.(
						response.data?.createFeedbackQuestion.feedbackQuestion as graphql.FeedbackQuestion,
					);
				} else {
					const response = await update({
						variables: { input: { id: props.item.id, text, type } },
					});

					props.onUpdate?.(
						response.data?.updateFeedbackQuestion.feedbackQuestion as graphql.FeedbackQuestion,
					);
				}

				setEditing(false);
			}}
		>
			{(active) => (
				<Card
					selected={active || props.selected}
					onClick={props.onSelect && (() => props.onSelect?.(props.item))}
					flexGrow={1}
					flexShrink={1}
					banner={
						{
							[graphql.FeedbackQuestionType.Stars]: 'yellow',
							[graphql.FeedbackQuestionType.Text]: 'employee',
							[graphql.FeedbackQuestionType.Both]: 'purple',
							// eslint-disable-next-line @typescript-eslint/no-explicit-any
						}[type] as any
					}
				>
					<Paragraph>
						{props.readOnly ? (
							localizedText
						) : active ? (
							<PlainInput
								onChange={setLocalizedText}
								onFocus={() => {
									setAutoFocus('text');
									setEditing(true);
								}}
								autoFocus={active && autoFocus === 'text'}
								value={localizedText}
							/>
						) : (
							<Box
								onMouseDown={() => {
									setAutoFocus('text');
									setEditing(true);
								}}
							>
								<Highlight query={props.query}>{localizedText ?? ''}</Highlight>
							</Box>
						)}
					</Paragraph>
					<Flex mt={2}>
						<Tags.Item.Select
							enabled={active}
							search={false}
							selectedValue={type}
							autoFocus={autoFocus === 'type'}
							onClick={
								props.readOnly
									? undefined
									: () => {
											setAutoFocus('type');
											setEditing(true);
									  }
							}
							onChange={(type) => setType(type as graphql.FeedbackQuestionType)}
							options={[
								{
									icon: <TextSizeIcon />,
									title: t('settings.feedbackLibrary.question.type.text'),
									value: graphql.FeedbackQuestionType.Text,
								},
								{
									icon: <StarIcon />,
									title: t('settings.feedbackLibrary.question.type.star'),
									value: graphql.FeedbackQuestionType.Stars,
								},
								{
									icon: <ContrastIcon />,
									title: t('settings.feedbackLibrary.question.type.both'),
									value: graphql.FeedbackQuestionType.Both,
								},
							]}
						/>
						{props.buttons}
					</Flex>
				</Card>
			)}
		</WorkingArea>
	);
}
